import React, { useState, useEffect } from 'react';
import { Modal, View, StyleSheet, Text } from 'react-native';
import { useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import CustomButton from '../../../../components/CustomButton';
import CustomDropDown from '../../../../components/CustomDropDown/CustomDropdown';
import { showMessage } from 'react-native-flash-message';
import Swal from 'sweetalert2';

const HRAdminCopyWorkflowSteps = ({
  visible,
  onClose,
  onConfirm,
  workflows,
  workflowObjs,
  targetWorkflowId,
  companyCode,
  selectedCategory,
  errorMessage,
  setErrorMessage,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = makeStyles(colors);

  const [selectedSourceWorkflowId, setSelectedSourceWorkflowId] = useState(null);

  useEffect(() => {
    if (!visible) {
      setSelectedSourceWorkflowId(null);
      setErrorMessage('');
    }
  }, [visible]);

  const handleConfirm = async () => {
    if (!selectedSourceWorkflowId) {
      setErrorMessage(t('common:pleaseSelectSourceWorkflow'));
      return;
    }
    if (selectedSourceWorkflowId === targetWorkflowId) {
      setErrorMessage(t('common:cannotCopyToSameWorkflow'));
      return;
    }
    setErrorMessage('');

    // Close your modal first
    onClose();

    const sourceName = workflowObjs[selectedSourceWorkflowId]?.description || selectedSourceWorkflowId;
    const targetName = workflowObjs[targetWorkflowId]?.description || targetWorkflowId;

    const message = t('common:areYouSureCopyWorkflowSteps', {
      sourceWorkflow: sourceName,
      targetWorkflow: targetName,
    });

    const result = await Swal.fire({
      title: message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('common:confirm'),
      cancelButtonText: t('common:cancel'),
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      onConfirm(selectedSourceWorkflowId);
      showMessage({
        message: t('common:workflowStepsCopiedSuccessfully'),
        type: 'success',
      });
    }
  };

  // Filter workflows by category to only show compatible source workflows
  const filteredWorkflows = workflows.filter(w => {
    const obj = workflowObjs[w.value];
    return obj && obj.categoryId === selectedCategory && w.value !== targetWorkflowId;
  });

  return (
    <Modal animationType="slide" transparent visible={visible} onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          <CustomLabel value={t('common:copyWorkflowSteps')} type="TITLE" style={{ textAlign: 'left' }} />
          {errorMessage ? (
            <View style={styles.errorMessageContainer}>
              <Text style={styles.errorMessageText}>{errorMessage}</Text>
            </View>
          ) : null}
          <View style={styles.row}>
            <View style={styles.subtitle}>
              <CustomLabel value={t('common:selectSourceWorkflow')} type="SUBTITLE" />
            </View>
            <View style={styles.dropdown}>
              <CustomDropDown
                multipleSelect={false}
                defValue={selectedSourceWorkflowId}
                value={selectedSourceWorkflowId}
                setValue={setSelectedSourceWorkflowId}
                options={filteredWorkflows}
                type="MAININPUT"
                disabled={false}
              />
            </View>
          </View>
          <View style={styles.modalButtons}>
            <CustomButton text={t('common:cancel')} onPress={onClose} type="BUTTON_CANCEL" sz={15} />
            <View style={{ width: 10 }} />
            <CustomButton text={t('common:copy')} onPress={handleConfirm} type="BUTTON_SAVE" isGradient sz={15} />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const makeStyles = (colors) =>
  StyleSheet.create({
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    modalContainer: {
      width: '90%',
      backgroundColor: 'white',
      borderRadius: 10,
      padding: 20,
    },
    modalButtons: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginTop: 20,
    },
    subtitle: {
      alignItems: 'flex-start',
      width: '30%',
      justifyContent: 'space-between',
      margin: 20,
    },
    dropdown: {
      flex: 1,
      justifyContent: 'flex-start',
      marginHorizontal: 8,
    },
    row: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      width: '95%',
      margin: 5,
    },
    errorMessageContainer: {
      backgroundColor: '#f8d7da',
      padding: 10,
      borderRadius: 5,
      marginVertical: 10,
    },
    errorMessageText: {
      color: '#721c24',
      fontWeight: 'bold',
    },
  });

export default HRAdminCopyWorkflowSteps;